import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useNotifications } from '../../../contexts/NotificationContext';
import { API_URL } from '../../../utils/api';
import useRoomMeasurements from './useRoomMeasurements';
import useEstimatData from './useEstimatData';
import useEstimatVariants from './useEstimatVariants';
import useDynamicNotes from './useDynamicNotes';
import useFeedbackActions from './useFeedbackActions';
import useEstimatForm from './useEstimatForm';
import useInlineEditing from './useInlineEditing';
import useEstimatSaveActions from './useEstimatSaveActions';
import useStandardval from './useStandardval';
import useExpandedSections from './useExpandedSections';
import useEstimatFollowup from './useEstimatFollowup';

/**
 * 🎯 Fasad-hook för EstimatPage
 * 
 * Samlar ALL setup-logik för EstimatPage på ett ställe.
 * Returnerar ett strukturerat API med 4 kategorier:
 * 
 * - `data`: Rådata (lead, customer, formData, estimat, etc.)
 * - `derived`: Beräknade värden (displayName, projectType, isActuallySent, etc.)
 * - `actions`: Funktioner (handleSave, handleSend, handleAddCustomItem, etc.)
 * - `status`: Status-flaggor (loading, saving, hasUnsavedChanges, etc.)
 */
const useEstimatPage = (leadId) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const notifications = useNotifications();
  
  // ==========================================
  // 📦 STATE - Metadata
  // ==========================================
  const [lead, setLead] = useState(null);
  const [customer, ] = useState(null);
  const [procurement, setProcurement] = useState(null);
  const [estimatFollowup, setEstimatFollowup] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasUnsavedNotes, setHasUnsavedNotes] = useState(false);
  const [originalEstimatKategorier, setOriginalEstimatKategorier] = useState(null);
  
  const [currentEstimatId, setCurrentEstimatId] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(1);
  const [currentStatus, setCurrentStatus] = useState('draft');
  const [currentSentAt, setCurrentSentAt] = useState(null);
  const [approvedByConsultant, setApprovedByConsultant] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  // Aktivt estimatspår. null = standardspåret, vilket är allt som finns på
  // leads utan parallella estimat.
  const [activeVariantKey, setActiveVariantKey] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [customerActivity, setCustomerActivity] = useState(null);
  const [showingAllActivities, setShowingAllActivities] = useState(false);
  const [isRotBeloppEditable, setIsRotBeloppEditable] = useState(false);
  const [bestallare2Registered, setBestallare2Registered] = useState(false);

  // ==========================================
  // 📦 STATE - Kommentarer & Feedback
  // ==========================================
  const [customerComments, setCustomerComments] = useState({});
  const [customerResponse, setCustomerResponse] = useState(null);
  const [savedComments, ] = useState({});
  const [expandedFeedbackItems, setExpandedFeedbackItems] = useState({});
  
  // ==========================================
  // 📦 STATE - Subkategorier (dragspel)
  // ==========================================
  const [expandedSubcategories, setExpandedSubcategories] = useState({});

  // ==========================================
  // 📦 STATE - SubItems (underrader expanderade)
  // ==========================================
  const [expandedSubItems, setExpandedSubItems] = useState({});
  
  // ==========================================
  // 📦 STATE - Modals & UI
  // ==========================================
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseModalData, setResponseModalData] = useState({ title: '', message: '', type: 'success' });
  const [shouldRefreshAfterModal, setShouldRefreshAfterModal] = useState(false);
  const [showSuggestTimeModal, setShowSuggestTimeModal] = useState(false);
  const [showBookMeetingModal, setShowBookMeetingModal] = useState(false);
  const [bookedFollowupSlot, setBookedFollowupSlot] = useState('');
  const [builderProposals, setBuilderProposals] = useState(null);
  const [bookingSlot, setBookingSlot] = useState('');
  const [followupTeamsUrl, setFollowupTeamsUrl] = useState('');
  const [showEstimatCustomerView, setShowEstimatCustomerView] = useState(false);
  const [showOverallThread, setShowOverallThread] = useState(false);
  const [initializedOverallThread, setInitializedOverallThread] = useState(false);
  const [showEstimatToCustomer, setShowEstimatToCustomer] = useState(false);
  // Default true — BK vill normalt visa presentationen, precis som modellens default.
  const [showForstudiePresentationToCustomer, setShowForstudiePresentationToCustomer] = useState(true);
  const [forstudiePresentationSprak, setForstudiePresentationSprak] = useState('sv');
  const [updatingVisibility, setUpdatingVisibility] = useState(false);
  const [updatingPresentationVisibility, setUpdatingPresentationVisibility] = useState(false);
  const [visaTidigareVersionerForKund, setVisaTidigareVersionerForKund] = useState(false);
  // Låsta baspaketandelar (shared/baspaketFordelning.js) — översikten ska visa samma som kunden
  const [baspaketAndelar, setBaspaketAndelar] = useState(null);
  const [uppdaterarTidigareVersioner, setUppdaterarTidigareVersioner] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState('');
  const [currentProjectType, ] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [modalData, setModalData] = useState({ customerName: '', projectType: '', bestallare2Name: '' });
  const [modalIsUpdate, setModalIsUpdate] = useState(false);
  
  // ==========================================
  // 🪝 HOOKS - Feature-specific
  // ==========================================
  
  // Expanderade sektioner (localStorage)
  const {
    expandedSections,
    expandedInfoSections,
    setExpandedSections,
    setExpandedInfoSections,
    toggleSection,
    toggleInfoSection,
    getStoredExpandedSections,
  } = useExpandedSections(leadId);
  
  // Rumsmått
  const {
    roomMeasurements,
    focusRoomId,
    setRoomMeasurements,
    addNewRoom,
    updateRoomField,
    reorderRooms,
    deleteRoom,
    toggleRoomEdit,
    clearFocusRoomId,
    saveRoomMeasurements: saveRoomMeasurementsFromHook,
  } = useRoomMeasurements(currentEstimatId, currentStatus, setHasUnsavedNotes);
  
  // Dynamiska anteckningar
  const {
    dynamicNotes,
    badgeCounts,
    focusNoteId,
    setDynamicNotes,
    setBadgeCounts,
    setFocusNoteId,
    addNewNote,
    updateNoteTitle,
    updateNoteContent,
    toggleTitleEdit,
    clearFocusNoteId,
  } = useDynamicNotes(setHasUnsavedNotes);
  
  // Standardval
  const {
    dynamicEstimatCategories,
    showAddStandardvalModal,
    showManageStandardvalModal,
    standardvalModalData,
    setShowAddStandardvalModal,
    setShowManageStandardvalModal,
    loadDynamicEstimatConfig,
    openAddStandardvalModal,
    handleSaveStandardval,
    handleStandardvalDeleted,
  } = useStandardval();
  
  // Form-hantering
  const {
    formData,
    setFormData,
    updateFormData,
    updateEstimat,
    handleAddCustomItem: handleAddCustomItemRaw,
    handleRemoveItem,
    handleReorderItems,
    // SubItems-funktioner
    addSubItem,
    updateSubItem,
    removeSubItem,
    toggleUseSubItemsTotal,
  } = useEstimatForm(
    {
      beställare2: { firstName: '', lastName: '', phone: '', email: '' },
      projectInfo: { budget: '', finansiering: '', rotAvdrag: 0, rotBelopp: 0, önskadStart: '', workDays: null, etableringPercent: 10 },
      intervjuFrågor: {
        talasOmOss: '', tidigareErfarenhet: '', genomförandeTid: '', vemBoIBostaden: '',
        förväntningar: '', kommunikation: '', parkeringsmöjligheter: ''
      },
      estimat: {}
    },
    dynamicEstimatCategories,
    setHasUnsavedChanges,
    setHasUnsavedNotes
  );
  
  // Inline-redigering
  const {
    editingLabels,
    tempLabelValues,
    setEditingLabels,
    setTempLabelValues,
    startEditLabel,
    cancelEditLabel,
    changeTempLabel,
    autoSaveLabel,
    editingPrices,
    tempPriceValues,
    startEditPrice,
    changeTempPrice,
    autoSavePrice,
  } = useInlineEditing(updateEstimat);
  
  // Feedback-actions
  const {
    newByggkonsultResponses,
    savedByggkonsultResponses,
    hasUnsavedResponses,
    showInlineComments,
    byggkonsultResponses,
    setNewByggkonsultResponses,
    setSavedByggkonsultResponses,
    setHasUnsavedResponses,
    setShowInlineComments,
    setByggkonsultResponses,
    handleByggkonsultResponseChange,
    handleMarkAsOK,
    handleSendAllResponses,
    handleSaveByggkonsultResponse,
    handleDeleteByggkonsultResponse,
    handleEditByggkonsultResponse,
    handleEditByggkonsultEntry,
    hasUnsavedChangesInResponse,
    toggleInlineComment,
    sendResponsestoCustomer,
  } = useFeedbackActions(currentEstimatId, setSaving, notifications);
  
  // Uppföljning (tidsförslag och bokningar)
  const {
    handleSuggestTime,
    handleBookMeeting,
  } = useEstimatFollowup({
    currentEstimatId,
    procurement,
    customer,
    lead,
    onSuccess: (data) => {
      setResponseModalData({
        title: data.title,
        message: data.message,
        type: data.type,
      });
      setShowResponseModal(true);
      if (data.shouldRefresh) {
        setShouldRefreshAfterModal(true);
      }
    },
    onError: (error) => {
      setResponseModalData({
        title: error.title,
        message: error.message,
        type: error.type,
      });
      setShowResponseModal(true);
    },
    setBookedFollowupSlot,
  });
  
  // ==========================================
  // 🔧 HELPER-FUNKTIONER
  // ==========================================
  
  // Effektivt kund-ID
  const getEffectiveCustomerId = useCallback(() => {
    let fromProc = null;
    if (procurement?.customerId) {
      if (typeof procurement.customerId === 'string') {
        fromProc = procurement.customerId;
      } else if (typeof procurement.customerId === 'object') {
        fromProc = procurement.customerId._id || procurement.customerId.id || null;
      }
    }
    return fromProc || customer?._id || lead?.convertedCustomerId || lead?.customerId || null;
  }, [procurement, customer, lead]);
  
  // Hämta kundaktivitet
  const fetchCustomerActivity = useCallback(async (estimatId, showAll = false) => {
    if (!estimatId) return;
    try {
      const token = localStorage.getItem('token');
      const limitParam = showAll ? '?limit=-1' : '';
      const response = await fetch(`${API_URL}/api/estimat/${estimatId}/customer-activity${limitParam}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setCustomerActivity(data);
      }
    } catch (error) {
      console.error('❌ Fel vid hämtning av kundaktivitet:', error);
    }
  }, []);
  
  // Visa alla aktiviteter
  const handleShowAllActivities = useCallback(async () => {
    if (currentEstimatId) {
      await fetchCustomerActivity(currentEstimatId, true);
      setShowingAllActivities(true);
    }
  }, [currentEstimatId, fetchCustomerActivity]);
  
  // Hantera versionsändringar
  const handleVersionChange = useCallback((newEstimat) => {
    if (newEstimat === null) {
      setTimeout(() => refetchData(), 200);
      return;
    }
    setCurrentEstimatId(newEstimat._id);
    setCurrentVersion(Number(newEstimat.version));
    setCurrentStatus(newEstimat.status);
    setCurrentSentAt(newEstimat.sentAt);
    setTimeout(() => refetchData(), 100);
    // refetchData utelämnad med avsikt: den destruktureras på rad ~632, alltså
    // EFTER den här callbacken. Att referera den i beroendelistan läses under
    // renderingen innan const tilldelats — alltså ReferenceError.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Avgöra om estimatet är skickat
  const isActuallySent = useCallback(() => {
    return currentSentAt && currentStatus !== 'draft';
  }, [currentSentAt, currentStatus]);
  
  // Avgöra om skicka-knappen ska vara inaktiv
  const shouldDisableSendButton = useCallback(() => {
    if (saving) return true;
    if (currentStatus === 'draft') {
      return hasUnsavedChanges;
    } else {
      return !hasUnsavedChanges;
    }
  }, [saving, currentStatus, hasUnsavedChanges]);
  
  // Wrapper för handleAddCustomItem med inline-editing fokus
  const handleAddCustomItem = useCallback((categoryKey, subcategoryKey = null) => {
    const labelKey = handleAddCustomItemRaw(categoryKey, subcategoryKey);
    setEditingLabels(prev => ({ ...prev, [labelKey]: true }));
    setTempLabelValues(prev => ({ ...prev, [labelKey]: '' }));
  }, [handleAddCustomItemRaw, setEditingLabels, setTempLabelValues]);
  
  // Toggla subkategori (dragspel)
  const toggleSubcategory = useCallback((categoryKey, subcategoryKey) => {
    setExpandedSubcategories(prev => ({
      ...prev,
      [`${categoryKey}_${subcategoryKey}`]: !prev[`${categoryKey}_${subcategoryKey}`]
    }));
  }, []);

  // Toggla subItems (underrader) för ett specifikt item
  const toggleSubItemsExpanded = useCallback((categoryKey, itemName) => {
    const key = `${categoryKey}_${itemName}`;
    setExpandedSubItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);
  
  // Formatera beställare2 för API
  const formatBeställare2ForAPI = useCallback((beställare2Data) => ({
    firstName: beställare2Data.firstName || '',
    lastName: beställare2Data.lastName || '',
    email: beställare2Data.email || '',
    phone: beställare2Data.phone || ''
  }), []);
  
  // Spara dynamiska anteckningar
  const saveDynamicNotes = useCallback(async () => {
    if (!currentEstimatId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/estimat/${currentEstimatId}/dynamic-notes`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dynamicNotes })
      });
      if (!response.ok) console.error('❌ Sparning misslyckades:', response.status);
    } catch (error) {
      console.error('❌ Sparning fel:', error);
    }
  }, [currentEstimatId, dynamicNotes]);
  
  // Spara intervjufrågor
  // Spara projektinfo on blur
  const saveProjectInfo = useCallback(async () => {
    if (!currentEstimatId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/estimat/${currentEstimatId}/project-info`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectInfo: formData.projectInfo })
      });
      // Flaggan nollställs INTE här. Den gäller hela formuläret, medan det här
      // anropet bara sparar projectInfo — nollställningen fick Spara-knappen att
      // tro att allt var sparat, och estimatposter och anteckningar försvann när
      // användaren lämnade sidan.
      if (!response.ok) {
        console.error('❌ Sparning av projektinfo misslyckades:', response.status);
      }
    } catch (error) {
      console.error('❌ Sparning av projektinfo fel:', error);
    }
  }, [currentEstimatId, formData.projectInfo]);

  // Spara beställare2 on blur
  const saveBestallare2 = useCallback(async () => {
    if (!currentEstimatId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/estimat/${currentEstimatId}/bestallare2`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ beställare2: formData.beställare2 })
      });
      // Se kommentaren i saveProjectInfo — samma fälla, samma skäl att låta bli
      if (!response.ok) {
        console.error('❌ Sparning av beställare2 misslyckades:', response.status);
      }
    } catch (error) {
      console.error('❌ Sparning av beställare2 fel:', error);
    }
  }, [currentEstimatId, formData.beställare2]);

  // Registrera beställare2 som användare i systemet (skapa konto + välkomstmail)
  const registerBestallare2 = useCallback(async () => {
    if (!currentEstimatId) return;
    // Efternamn krävs (Customer-modellen kräver lastName) — blockera annars
    const b2 = formData?.beställare2;
    if (!b2?.firstName?.trim() || !b2?.lastName?.trim()) {
      notifications.showError(
        'Efternamn saknas',
        'Beställare 2 måste ha både förnamn och efternamn innan användaren kan skapas.'
      );
      return;
    }
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/estimat/${currentEstimatId}/register-bestallare2`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Fel vid registrering');

      setBestallare2Registered(true);
      notifications.showSuccess(
        'Beställare 2 registrerad',
        data.message
      );
    } catch (error) {
      console.error('❌ Fel vid registrering av beställare2:', error);
      notifications.showError(
        'Kunde inte registrera',
        error.message || 'Ett fel uppstod vid registrering av beställare 2.'
      );
    } finally {
      setSaving(false);
    }
  }, [currentEstimatId, notifications, formData?.beställare2]);

  // Spara intervjufrågor on blur
  const saveIntervjuFragor = useCallback(async () => {
    if (!currentEstimatId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/estimat/${currentEstimatId}/intervju-fragor`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ intervjuFrågor: formData.intervjuFrågor })
      });
      if (response.ok) {
        setHasUnsavedNotes(false);
      }
    } catch (error) {
      console.error('❌ Sparning av intervjufrågor fel:', error);
    }
  }, [currentEstimatId, formData.intervjuFrågor, setHasUnsavedNotes]);
  
  // Hantera dynamisk anteckningssparande (on blur)
  const handleDynamicNoteSave = useCallback(async () => {
    if (!currentEstimatId) {
      setHasUnsavedNotes(true);
      return;
    }
    await saveDynamicNotes();
    setHasUnsavedNotes(false);
  }, [currentEstimatId, saveDynamicNotes]);

  // Ta bort dynamisk anteckning och spara till DB direkt
  const handleDeleteNote = useCallback(async (id) => {
    const updatedNotes = dynamicNotes.filter(note => note.id !== id);
    setDynamicNotes(updatedNotes);
    if (currentEstimatId) {
      try {
        const token = localStorage.getItem('token');
        await fetch(`${API_URL}/api/estimat/${currentEstimatId}/dynamic-notes`, {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ dynamicNotes: updatedNotes })
        });
      } catch (error) {
        console.error('❌ Radering av anteckning fel:', error);
      }
    }
  }, [dynamicNotes, currentEstimatId, setDynamicNotes]);
  
  // Cache invalidering
  const invalidateCache = useCallback(async () => {
    try {
      const { invalidateGlobalCache } = await import('../../../hooks/useApiCache');
      invalidateGlobalCache([
        'dashboard_users', 'booked_leads', 'assigned_projects',
        'procurement_items', 'dashboard_projects', 'dashboard_builders', 'dashboard_suppliers'
      ]);
    } catch (error) {
      console.warn('Kunde inte invalidera cache:', error);
    }
  }, []);
  
  // Hantera synlighetsändring
  const handleVisibilityChange = useCallback(async (newVisibility) => {
    if (!currentEstimatId) return;
    setUpdatingVisibility(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/estimat/${currentEstimatId}/visibility`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ showEstimatToCustomer: newVisibility })
      });
      if (response.ok) {
        const data = await response.json();
        setShowEstimatToCustomer(data.showEstimatToCustomer);
      } else {
        setShowEstimatToCustomer(!newVisibility);
      }
    } catch (error) {
      setShowEstimatToCustomer(!newVisibility);
    } finally {
      setUpdatingVisibility(false);
    }
  }, [currentEstimatId]);

  // Hantera synlighetsändring för förstudie-presentation
  /**
   * Byter språk på förstudiepresentationen. Samma endpoint som synligheten —
   * valet ligger på estimatet så kundens länk får samma språk som mötet hölls på.
   */
  const handlePresentationSprakChange = useCallback(async (nyttSprak) => {
    if (!currentEstimatId || !['sv', 'en'].includes(nyttSprak)) return;
    const foregaende = forstudiePresentationSprak;
    setForstudiePresentationSprak(nyttSprak);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/estimat/${currentEstimatId}/visibility`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ forstudiePresentationSprak: nyttSprak })
      });
      if (!response.ok) setForstudiePresentationSprak(foregaende);
    } catch (error) {
      setForstudiePresentationSprak(foregaende);
    }
  }, [currentEstimatId, forstudiePresentationSprak]);

  const handlePresentationVisibilityChange = useCallback(async (newVisibility) => {
    if (!currentEstimatId) return;
    setUpdatingPresentationVisibility(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/estimat/${currentEstimatId}/visibility`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ showForstudiePresentationToCustomer: newVisibility })
      });
      if (response.ok) {
        const data = await response.json();
        setShowForstudiePresentationToCustomer(data.showForstudiePresentationToCustomer);
      } else {
        setShowForstudiePresentationToCustomer(!newVisibility);
      }
    } catch (error) {
      setShowForstudiePresentationToCustomer(!newVisibility);
    } finally {
      setUpdatingPresentationVisibility(false);
    }
  }, [currentEstimatId]);

  // Får kunden öppna tidigare skickade versioner (läsläge)?
  const handleTidigareVersionerChange = useCallback(async (nyttVarde) => {
    if (!currentEstimatId) return;
    setUppdaterarTidigareVersioner(true);
    setVisaTidigareVersionerForKund(nyttVarde);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/estimat/${currentEstimatId}/visibility`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ visaTidigareVersionerForKund: nyttVarde })
      });
      if (response.ok) {
        const data = await response.json();
        setVisaTidigareVersionerForKund(data.visaTidigareVersionerForKund === true);
      } else {
        setVisaTidigareVersionerForKund(!nyttVarde);
      }
    } catch (error) {
      setVisaTidigareVersionerForKund(!nyttVarde);
    } finally {
      setUppdaterarTidigareVersioner(false);
    }
  }, [currentEstimatId]);

  // Hantera stängning av success modal
  const handleCloseSuccessModal = useCallback(() => {
    setShowSuccessModal(false);
    navigate('/workspace/oversikt');
  }, [navigate]);
  
  // ==========================================
  // 🪝 SAVE/SEND ACTIONS
  // ==========================================
  
  const { handleSaveEstimat, handleSendEstimat } = useEstimatSaveActions({
    variantKey: activeVariantKey,
    leadId, currentEstimatId, currentVersion, currentStatus, currentProjectId, formData,
    dynamicNotes, roomMeasurements, originalEstimatKategorier, hasUnsavedChanges, hasUnsavedNotes,
    setCurrentEstimatId, setCurrentVersion, setCurrentStatus, setCurrentSentAt, setCurrentProjectId,
    setOriginalEstimatKategorier, setHasUnsavedChanges, setHasUnsavedNotes, setSaving,
    saveDynamicNotes, saveIntervjuFragor, isActuallySent, formatBeställare2ForAPI,
    invalidateCache, navigate, setShowSuccessModal, setModalIsUpdate
  });
  
  // ==========================================
  // 🪝 DATA FETCHING
  // ==========================================
  
  const estimatDataCallbacks = useMemo(() => ({
    setProcurement, setCurrentEstimatId, setCurrentVersion, setCurrentStatus, setCurrentSentAt,
    setCustomerId, setCurrentProjectId, setApprovedByConsultant, setShowEstimatToCustomer, setShowForstudiePresentationToCustomer, setForstudiePresentationSprak, setBestallare2Registered,
    setVisaTidigareVersionerForKund, setBaspaketAndelar,
    setEstimatFollowup, setBookedFollowupSlot, setFollowupTeamsUrl, setFormData,
    setOriginalEstimatKategorier, setDynamicNotes, setBadgeCounts, setRoomMeasurements,
    setCustomerResponse, setCustomerComments, setByggkonsultResponses, setBuilderProposals,
    setLead, fetchCustomerActivity, setShowingAllActivities, procurement
  }), [procurement, fetchCustomerActivity, setBadgeCounts, setByggkonsultResponses, setDynamicNotes, setFormData, setRoomMeasurements]);
  
  const {
    varianter, dupliceringPagar, hamtaVarianter, duplicera, harFleraSpar
  } = useEstimatVariants(leadId);

  const { loading: estimatLoading,  refetchData } = useEstimatData(leadId, estimatDataCallbacks, activeVariantKey);

  // Välj ett spår som faktiskt finns.
  //
  // activeVariantKey startar som null (standardspåret), men det spåret behöver
  // inte existera — raderar man originalet finns bara namngivna estimat kvar.
  // Då hittade laddningen ingenting, vyn trodde att leadet saknade estimat, och
  // autosparet skapade ett nytt tomt "Estimat 1.0" ur tomma intet.
  useEffect(() => {
    if (!varianter.length) return;
    const finns = varianter.some(v => (v.variantKey ?? null) === (activeVariantKey ?? null));
    if (!finns) {
      setActiveVariantKey(varianter[0].variantKey ?? null);
    }
  }, [varianter, activeVariantKey]);

  // Duplicera och hoppa direkt till kopian — det är den man vill jobba i.
  const dupliceraEstimat = useCallback(async (variantName) => {
    const nytt = await duplicera(currentEstimatId, variantName);
    setShowDuplicateModal(false);
    setActiveVariantKey(nytt?.variantKey || null);
    return nytt;
  }, [duplicera, currentEstimatId]);
  
  useEffect(() => {
    setLoading(estimatLoading);
  }, [estimatLoading]);
  
  // ==========================================
  // 🔄 SIDE-EFFECTS
  // ==========================================
  
  // Ladda dynamisk estimat-config
  useEffect(() => {
    loadDynamicEstimatConfig();
  }, [loadDynamicEstimatConfig]);
  
  // Initialisera estimat med alla kategorier
  useEffect(() => {
    const initialEstimat = {};
    const initialExpanded = {};
    
    Object.keys(dynamicEstimatCategories).forEach(categoryKey => {
      const category = dynamicEstimatCategories[categoryKey];
      initialEstimat[categoryKey] = {};
      
      if (category.subcategories) {
        Object.keys(category.subcategories).forEach(subcategoryKey => {
          const subcategory = category.subcategories[subcategoryKey];
          subcategory.items.forEach(item => {
            const itemKey = `${subcategoryKey}_${item.name}`;
            initialEstimat[categoryKey][itemKey] = {
              selected: false, quantity: item.defaultQuantity, unit: item.unit,
              price: item.price, label: item.name
            };
          });
        });
      } else if (category.items) {
        category.items.forEach(item => {
          initialEstimat[categoryKey][item.name] = {
            selected: false, quantity: item.defaultQuantity, unit: item.unit,
            price: item.price, label: item.name
          };
        });
      }
      initialExpanded[categoryKey] = false;
    });
    
    setFormData(prev => {
      const mergedEstimat = { ...prev.estimat };
      Object.keys(initialEstimat).forEach(categoryKey => {
        if (!mergedEstimat[categoryKey]) mergedEstimat[categoryKey] = {};
        Object.keys(initialEstimat[categoryKey]).forEach(itemKey => {
          if (!mergedEstimat[categoryKey][itemKey]) {
            mergedEstimat[categoryKey][itemKey] = initialEstimat[categoryKey][itemKey];
          }
        });
      });
      return { ...prev, estimat: mergedEstimat };
    });
    
    const storedExpanded = getStoredExpandedSections();
    const mergedExpanded = { ...initialExpanded, ...storedExpanded };
    setExpandedSections(mergedExpanded);
    
    const initialSubs = {};
    const byggoffert = dynamicEstimatCategories['byggoffert'];
    if (byggoffert?.subcategories) {
      Object.keys(byggoffert.subcategories).forEach(scKey => {
        initialSubs[`byggoffert_${scKey}`] = scKey === 'invändigt';
      });
    }
    setExpandedSubcategories(initialSubs);
  }, [dynamicEstimatCategories, getStoredExpandedSections, setExpandedSections, setFormData]);
  
  // Populera från lead-data
  useEffect(() => {
    if (lead) {
      setFormData(prev => {
        const updatedData = { ...prev };
        
        if (lead.beställare2) {
          const leadBest2 = lead.beställare2;
          const currentBest2 = prev.beställare2 || {};
          const isEmptyEstimatB2 = !currentBest2.firstName && !currentBest2.lastName && !currentBest2.email && !currentBest2.phone;
          
          if (isEmptyEstimatB2) {
            const firstName = leadBest2.firstName || '';
            const lastName = leadBest2.lastName || '';
            const email = leadBest2.email || '';
            const phone = leadBest2.phone || '';
            
            if (firstName || lastName || email || phone) {
              updatedData.beställare2 = { ...currentBest2, firstName, lastName, email, phone };
            }
          }
        }
        
        if (lead.projectInfo) {
          const updatedProjectInfo = { ...prev.projectInfo };
          if (lead.projectInfo.budget && !prev.projectInfo.budget) {
            updatedProjectInfo.budget = lead.projectInfo.budget;
          }
          if (lead.projectInfo.startDate && !prev.projectInfo.önskadStart) {
            updatedProjectInfo.önskadStart = lead.projectInfo.startDate;
          }
          updatedData.projectInfo = updatedProjectInfo;
        }
        
        return updatedData;
      });
      
      const shouldMarkChanged = 
        ((lead.beställare2?.firstName || lead.beställare2?.lastName || lead.beställare2?.email || lead.beställare2?.phone) && !formData.beställare2?.firstName) ||
        (lead.projectInfo?.budget && !formData.projectInfo?.budget) || 
        (lead.projectInfo?.startDate && !formData.projectInfo?.önskadStart);
        
      if (shouldMarkChanged) setHasUnsavedChanges(true);
    }
    // formData-fälten och setFormData utelämnade med avsikt. Effekten fyller i
    // tomma fält från lead när det anländer, och LÄSER formData bara för att
    // avgöra om något faktiskt ändrades. Att ta med dem hade fått effekten att
    // köra om vid varje formulärändring och upprepa setHasUnsavedChanges(true).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead]);
  
  // Uppdatera modalData med kundinfo för success-modal
  useEffect(() => {
    if (lead || customer || formData) {
      const customerName = customer
        ? `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || customer.email || 'Kund'
        // Föredra firstName+lastName framför det gamla `name`-fältet (kan spreta efter beställare-byte)
        : (`${lead?.customerInfo?.firstName || ''} ${lead?.customerInfo?.lastName || ''}`.trim()
            || lead?.customerInfo?.name || 'Kund');
      
      const projectType = lead?.projectInfo?.projectType || currentProjectType || 'projektet';
      
      const bestallare2Name = formData?.beställare2
        ? `${formData.beställare2.firstName || ''} ${formData.beställare2.lastName || ''}`.trim()
        : '';
      
      setModalData({
        customerName,
        projectType,
        bestallare2Name
      });
    }
  }, [lead, customer, formData, currentProjectType]);
  
  // Ctrl+S för sparande
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        if (handleSaveEstimat && !saving) handleSaveEstimat();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleSaveEstimat, saving]);
  
  // ESC för stängning av presentationsläge
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showEstimatCustomerView) {
        setShowEstimatCustomerView(false);
      }
    };
    if (showEstimatCustomerView) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [showEstimatCustomerView]);
  
  // Initiera overall thread
  useEffect(() => {
    if (initializedOverallThread) return;
    try {
      const hasOverall = Boolean(customerComments && customerComments.__overall__);
      if (!hasOverall) return;
      const consObj = byggkonsultResponses?.__overall__;
      const hasConsultantReply = !!(consObj && (
        (typeof consObj === 'string' && consObj.trim()) ||
        (typeof consObj === 'object' && ((consObj?.text && String(consObj.text).trim()) || (Array.isArray(consObj.thread) && consObj.thread.length > 0)))
      ));
      setShowOverallThread(!hasConsultantReply);
      setInitializedOverallThread(true);
    } catch (_) {}
  }, [customerComments, byggkonsultResponses, initializedOverallThread]);
  
  // ==========================================
  // 📊 DERIVED STATE (Beräknade värden)
  // ==========================================
  
  const derived = useMemo(() => {
    const projectData = lead || customer;
    
    // Display name
    const leadName = (lead?.customerInfo?.name || '').trim();
    const leadFirstLast = `${lead?.customerInfo?.firstName || ''} ${lead?.customerInfo?.lastName || ''}`.trim();
    const customerFull = `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim();
    // Föredra firstName+lastName framför det gamla `name`-fältet, som kan spreta efter
    // ett beställare-byte (beställare 1 → beställare 2) och då visa fel person.
    const displayName = leadFirstLast || leadName || customerFull || 'Okänd kund';
    
    // Project type
    const projectType = lead ? 
      (lead.projectInfo?.projectType || 'Projekttyp ej angiven') : 
      (customer?.customerProjectInfo?.projectType || 'Projekttyp ej angiven');
    
    // Project ID
    const projectPublicId = lead?.projectId || customer?.customerProjectInfo?.projectId || '';
    
    return {
      projectData,
      displayName,
      projectType,
      projectPublicId,
      isActuallySent: isActuallySent(),
      shouldDisableSendButton: shouldDisableSendButton(),
    };
  }, [lead, customer, isActuallySent, shouldDisableSendButton]);
  
  // ==========================================
  // 🎯 RETURN - Fasad API
  // ==========================================
  
  return {
    // 📦 Data
    data: {
      lead,
      customer,
      procurement,
      estimatFollowup,
      formData,
      dynamicNotes,
      roomMeasurements,
      dynamicEstimatCategories,
      customerComments,
      customerResponse,
      savedComments,
      customerActivity,
      showingAllActivities,
      expandedSections,
      expandedInfoSections,
      expandedSubcategories,
      expandedSubItems,
      expandedFeedbackItems,
      builderProposals,
      byggkonsultResponses,
      newByggkonsultResponses,
      savedByggkonsultResponses,
      badgeCounts,
      focusNoteId,
      focusRoomId,
      editingLabels,
      tempLabelValues,
      editingPrices,
      tempPriceValues,
      originalEstimatKategorier,
      currentEstimatId,
      customerId,
      currentVersion,
      currentStatus,
      currentSentAt,
      currentProjectId,
      currentProjectType,
      approvedByConsultant,
      isRotBeloppEditable,
      showEstimatToCustomer,
      updatingVisibility,
      showForstudiePresentationToCustomer,
      forstudiePresentationSprak,
      updatingPresentationVisibility,
      visaTidigareVersionerForKund,
      uppdaterarTidigareVersioner,
      baspaketAndelar,
      bestallare2Registered,
    },
    
    // 📊 Derived (Beräknade värden)
    derived,
    
    // 🎬 Actions
    actions: {
      // Form actions
      updateFormData,
      updateEstimat,
      handleAddCustomItem,
      handleRemoveItem,
      handleReorderItems,

      // SubItem actions
      addSubItem,
      updateSubItem,
      removeSubItem,
      toggleUseSubItemsTotal,
      toggleSubItemsExpanded,
      
      // Save/Send actions
      handleSaveEstimat,
      handleSendEstimat,
      handleCloseSuccessModal,
      
      // Note actions
      addNewNote,
      updateNoteTitle,
      updateNoteContent,
      deleteNote: handleDeleteNote,
      toggleTitleEdit,
      clearFocusNoteId,
      handleDynamicNoteSave,
      saveIntervjuFragor,
      saveProjectInfo,
      saveBestallare2,
      registerBestallare2,

      // Room measurement actions
      addNewRoom,
      updateRoomField,
      reorderRooms,
      deleteRoom,
      toggleRoomEdit,
      clearFocusRoomId: clearFocusRoomId,
      saveRoomMeasurements: saveRoomMeasurementsFromHook,
      
      // Section toggles
      toggleSection,
      toggleInfoSection,
      toggleSubcategory,
      
      // Inline editing
      startEditLabel,
      cancelEditLabel,
      changeTempLabel,
      autoSaveLabel,
      startEditPrice,
      changeTempPrice,
      autoSavePrice,
      
      // Feedback actions
      handleByggkonsultResponseChange,
      handleMarkAsOK,
      handleSendAllResponses,
      handleSaveByggkonsultResponse,
      handleDeleteByggkonsultResponse,
      handleEditByggkonsultResponse,
      handleEditByggkonsultEntry,
      hasUnsavedChangesInResponse,
      toggleInlineComment,
      sendResponsestoCustomer,
      
      // Standardval actions
      openAddStandardvalModal,
      handleSaveStandardval,
      handleStandardvalDeleted,
      
      // Uppföljning actions
      handleSuggestTime,
      handleBookMeeting,
      
      // Customer activity
      handleShowAllActivities,
      
      // Version control
      handleVersionChange,
      
      // Visibility
      handleVisibilityChange,
      handlePresentationVisibilityChange,
      handlePresentationSprakChange,
      handleTidigareVersionerChange,

      // Navigation
      navigate,
      
      // Misc
      refetchData,
      invalidateCache,
    },
    
    // 🚦 Status
    status: {
      loading,
      saving,
      hasUnsavedChanges,
      hasUnsavedNotes,
      hasUnsavedResponses,
      showInlineComments,
      showVersionModal,
      showDuplicateModal,
      dupliceringPagar,
      showResponseModal,
      responseModalData,
      shouldRefreshAfterModal,
      showSuggestTimeModal,
      showBookMeetingModal,
      bookedFollowupSlot,
      bookingSlot,
      followupTeamsUrl,
      showEstimatCustomerView,
      showOverallThread,
      initializedOverallThread,
      showSuccessModal,
      modalData,
      modalIsUpdate,
      showAddStandardvalModal,
      showManageStandardvalModal,
      standardvalModalData,
    },

    // 📑 Parallella estimat på samma lead
    varianter: {
      lista: varianter,
      aktivNyckel: activeVariantKey,
      harFleraSpar,
      duplicera: dupliceraEstimat,
      uppdatera: hamtaVarianter
    },
    
    // 🎛️ Setters (för special cases)
    setters: {
      setShowVersionModal,
      setShowDuplicateModal,
      setActiveVariantKey,
      setShowResponseModal,
      setResponseModalData,
      setShouldRefreshAfterModal,
      setShowSuggestTimeModal,
      setShowBookMeetingModal,
      setBookedFollowupSlot,
      setBookingSlot,
      setFollowupTeamsUrl,
      setShowEstimatCustomerView,
      setShowOverallThread,
      setInitializedOverallThread,
      setIsRotBeloppEditable,
      setExpandedFeedbackItems,
      setShowAddStandardvalModal,
      setShowManageStandardvalModal,
      setExpandedInfoSections,
      setEditingLabels,
      setTempLabelValues,
      setNewByggkonsultResponses,
      setSavedByggkonsultResponses,
      setHasUnsavedResponses,
      setShowInlineComments,
      setDynamicNotes,
      setBadgeCounts,
      setFocusNoteId,
    },
    
    // 🔧 Utils
    utils: {
      getEffectiveCustomerId,
      formatBeställare2ForAPI,
      user,
    },
  };
};

export default useEstimatPage;
